import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { collectedOf, compareCollectionRows, effectiveStatus, isCollected, outstandingOf } from '../utils/billStatus';
import DutyTypePill from '../components/DutyTypePill';
import CollectionFilters, { ColumnFilters, matchesCollectionFilters } from '../components/CollectionFilters';
import { BILL_TYPE_META } from '../constants/billingTypes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

interface MonthlyBill {
  id: number;
  bill_no: number | null;
  invoice_no?: string | null;
  bill_date: string;
  invoice_period_start: string;
  invoice_period_end: string;
  total_amount: number;
  amount_received: number;
  status: string;
  bill_type: string | null;
  patient_id: number;
  patient_name: string;
  client_name: string | null;
  /** Every client on the case — a patient can have more than one. */
  client_names?: string[];
  contact: string | null;
  receipt_pdf_url: string | null;
  has_payment_receipt: boolean;
  is_carryover: boolean;
  duty_type: string | null;
  case_manager: string | null;
  expected_payment_date: string | null;
}

function fmt(dateStr: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// dd-mm-yyyy for the sales report (matches the printed Excel format)
function fmtDMY(dateStr: string | null) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}

// bill_type → human "Service" label shown on the sales report
const SERVICE_LABEL: Record<string, string> = {
  nursing:           'Nursing Service',
  nursing_visit:     'Nursing Service',
  physio:            'Physiotherapy',
  doctor_visit:      'Doctor Visit',
  medical_equipment: 'Medical Equipment',
  equipment_rental:  'Medical Equipment',
  equipment_sale:    'Medical Equipment',
  consumables:       'Consumables',
  consumable_return: 'Consumables Return',
  misc:              'Miscellaneous',
};

function fmtAmount(n: number) {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function monthLabel(ym: string) {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function prevMonth(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonth(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const statusStyle = (status: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string }> = {
    Pending:   { bg: '#fef3c7', color: '#92400e' },
    Collected: { bg: '#dcfce7', color: '#15803d' },
    Processed: { bg: '#dbeafe', color: '#1e40af' },
    // Consumable returns: a credit note, adjusted rather than collected.
    Completed: { bg: '#f1f5f9', color: '#334155' },
  };
  const s = map[status] || { bg: '#f1f5f9', color: '#475569' };
  return {
    display: 'inline-block', padding: '3px 10px', borderRadius: '9999px',
    fontSize: '0.78rem', fontWeight: 600, backgroundColor: s.bg, color: s.color,
  };
};

const PaymentCollectionPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(currentYearMonth());
  const [bills, setBills] = useState<MonthlyBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // A filter set against one month means nothing in the next — a case manager
  // or expected-payment range from August would silently empty September.
  useEffect(() => {
    setColumnFilters({});
    setDateFrom('');
    setDateTo('');
  }, [selectedMonth]);

  // Export popover (choose Collected / Pending / both)
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportCollected, setExportCollected] = useState(true);
  const [exportPending, setExportPending] = useState(true);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/billing/monthly-bills`, {
        params: { month: selectedMonth },
        headers,
      });
      setBills(res.data);
    } catch {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchBills(); }, [fetchBills]);

  // ── Download the month's Collection Report (Excel, matches the printed LHHS format) ──
  // includeCollected → bills that received money (full or partial)
  // includePending   → bills that still owe money (partial + fully unpaid)
  const downloadSalesReport = async (includeCollected: boolean, includePending: boolean) => {
    if (!includeCollected && !includePending) {
      toast.error('Pick at least one — Collected or Pending');
      return;
    }

    const rows = bills
      .filter((b) => {
        const collected = b.amount_received || 0;
        const pending = Math.max((b.total_amount || 0) - collected, 0);
        const isCollected = collected > 0;            // got some money (incl. partial)
        const isPending = pending > 0.009;            // still owes (incl. partial / unpaid)
        return (includeCollected && isCollected) || (includePending && isPending);
      })
      // Consumables have no bill_no (invoice_no is their number) — sort them last
      // rather than letting null arithmetic produce NaN and scramble the order.
      .sort((a, b) => (a.bill_no ?? Infinity) - (b.bill_no ?? Infinity));

    if (rows.length === 0) {
      toast.error('No matching bills in this month to export');
      return;
    }

    setShowExportMenu(false);

    const kind = includeCollected && includePending ? 'COLLECTIONS & PENDING'
      : includeCollected ? 'COLLECTIONS' : 'PENDING';
    const monthTitle = monthLabel(selectedMonth).toUpperCase(); // e.g. "MAY 2026"
    const NCOLS = 12;
    const AMT_COLS = [8, 9, 10]; // Bill Amount, Collected, Pending
    const thin = { style: 'thin' as const };
    const border = { top: thin, left: thin, bottom: thin, right: thin };

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Collections');
    ws.columns = [
      { width: 6 }, { width: 13 }, { width: 18 }, { width: 36 }, { width: 16 },
      { width: 13 }, { width: 13 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 11 }, { width: 22 },
    ];

    // Title row — "COLLECTIONS / LHHS / MAY 2026"
    ws.mergeCells(1, 1, 1, NCOLS);
    const titleCell = ws.getCell(1, 1);
    titleCell.value = `${kind} / LHHS / ${monthTitle}`;
    titleCell.font = { bold: true, size: 12 };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2DCDB' } };
    ws.getRow(1).height = 24;
    for (let c = 1; c <= NCOLS; c++) ws.getCell(1, c).border = border;

    // Header row
    const headerRow = ws.addRow([
      'S/L', 'DATE', 'INVOICE', 'CUSTOMER', 'SERVICE', 'START', 'END',
      'Bill Amount', 'Collected', 'Pending', 'Status', 'Remarks',
    ]);
    headerRow.height = 20;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDD9C3' } };
      cell.border = border;
    });

    // Data rows
    let totalBill = 0, totalCollected = 0, totalPending = 0;
    rows.forEach((b, i) => {
      const bill = b.total_amount || 0;
      const collected = b.amount_received || 0;
      const pending = Math.max(bill - collected, 0);
      totalBill += bill; totalCollected += collected; totalPending += pending;

      // Status: nothing received → Pending; some received but balance left → Partial; balance clear → Collected
      const statusLabel = collected <= 0.009 ? 'Pending' : (pending > 0.009 ? 'Partial' : 'Collected');
      const statusColor = statusLabel === 'Collected' ? 'FF15803D' : statusLabel === 'Partial' ? 'FFB45309' : 'FFDC2626';

      // Carry-over = bill raised in an earlier month, still being settled this month
      const billMonth = b.bill_date
        ? new Date(b.bill_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
        : '';
      const remark = b.is_carryover ? `Carry-over (from ${billMonth})` : '';

      const row = ws.addRow([
        i + 1,
        fmtDMY(b.bill_date),
        b.invoice_no || b.bill_no,
        b.client_name || b.patient_name || '',
        SERVICE_LABEL[b.bill_type || ''] || 'Nursing Service',
        fmtDMY(b.invoice_period_start),
        fmtDMY(b.invoice_period_end),
        bill,
        collected,
        pending,
        statusLabel,
        remark,
      ]);
      row.eachCell((cell, col) => {
        cell.border = border;
        cell.alignment = { horizontal: col === 12 ? 'left' : 'center', vertical: 'middle', wrapText: col === 4 };
        if (b.is_carryover) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7E0' } }; // amber tint
        if (AMT_COLS.includes(col)) cell.numFmt = '#,##,##0.00';
        if (col === 10 && pending > 0.009) cell.font = { color: { argb: 'FFB45309' } }; // pending amount in amber
        if (col === 11) cell.font = { bold: true, color: { argb: statusColor } };
        if (col === 12 && b.is_carryover) cell.font = { italic: true, color: { argb: 'FFB45309' } };
      });
    });

    // Grand total row — green band
    const gt = ws.addRow([]);
    ws.mergeCells(gt.number, 1, gt.number, 7);
    const gtLabel = ws.getCell(gt.number, 1);
    gtLabel.value = 'Grand Total Amount INR';
    gtLabel.font = { bold: true };
    gtLabel.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getCell(gt.number, 8).value = totalBill;
    ws.getCell(gt.number, 9).value = totalCollected;
    ws.getCell(gt.number, 10).value = totalPending;
    AMT_COLS.forEach((c) => {
      const cell = ws.getCell(gt.number, c);
      cell.numFmt = '#,##,##0.00';
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    for (let c = 1; c <= NCOLS; c++) {
      const cell = ws.getCell(gt.number, c);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF92D050' } };
      cell.border = border;
    }

    const buffer = await wb.xlsx.writeBuffer();
    const fileKind = includeCollected && includePending ? 'Collections & Pending'
      : includeCollected ? 'Collections' : 'Pending';
    saveAs(new Blob([buffer]), `${fileKind} Report - LHHS - ${monthTitle}.xlsx`);
  };

  const filtered = bills
    .filter((b) => {
      const term = search.toLowerCase();
      const matchSearch =
        !term ||
        b.patient_name.toLowerCase().includes(term) ||
        (b.client_name || '').toLowerCase().includes(term) ||
        `#${b.bill_no}`.includes(term) ||
        (b.invoice_no || '').toLowerCase().includes(term) ||
        `LHHS${b.patient_id}`.toLowerCase().includes(term);

      return matchSearch && matchesCollectionFilters(b, columnFilters, dateFrom, dateTo);
    })
    // Settled rows last, then duty type, then A→Z on the client — the same
    // order Current Payment Collections uses. The bill-no tie-break keeps two
    // bills of one client and duty from swapping between renders.
    .sort((a, b) => compareCollectionRows(a, b) || (a.bill_no ?? Infinity) - (b.bill_no ?? Infinity));

  // Money is summed per bill, not per bucket — see the same note on Current
  // Payment Collections. Putting a bill's whole amount in one bucket reported
  // a part-paid bill as fully pending, hiding what had already been received.
  const totalAmount = filtered.reduce((s, b) => s + b.total_amount, 0);
  const totalCollected = filtered.reduce((s, b) => s + collectedOf(b), 0);
  const totalPending = filtered.reduce((s, b) => s + outstandingOf(b), 0);
  // Counts stay per bill, off the derived status, so they match the table and
  // the filter buttons.
  const pendingCount = filtered.filter((b) => !isCollected(b)).length;
  const collectedCount = filtered.filter((b) => isCollected(b)).length;

  const isCurrentMonth = selectedMonth === currentYearMonth();

  return (
    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Page header */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' }}>
            Payment Collections
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
            Track monthly bill payments across all patients
          </p>
        </div>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowExportMenu((v) => !v)}
            style={{
              padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              fontSize: '0.88rem', fontWeight: 600, color: 'white',
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              boxShadow: '0 4px 6px -1px rgba(22,163,74,0.25)',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
            </svg>
            Download Report
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showExportMenu && (
            <>
              {/* click-away catcher */}
              <div
                onClick={() => setShowExportMenu(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div
                style={{
                  position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 41,
                  background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.14)', padding: '1rem', width: 250,
                }}
              >
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Include in report
                </div>
                {([
                  { key: 'collected', label: 'Collected', desc: 'Bills with money received (incl. partial)', checked: exportCollected, set: setExportCollected },
                  { key: 'pending', label: 'Pending', desc: 'Bills with an outstanding balance', checked: exportPending, set: setExportPending },
                ] as const).map((opt) => (
                  <label
                    key={opt.key}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer',
                      padding: '8px', borderRadius: '8px',
                      background: opt.checked ? '#f0fdf4' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={opt.checked}
                      onChange={(e) => opt.set(e.target.checked)}
                      style={{ marginTop: '2px', width: 16, height: 16, accentColor: '#16a34a', cursor: 'pointer' }}
                    />
                    <span>
                      <span style={{ display: 'block', fontSize: '0.86rem', fontWeight: 600, color: '#0f172a' }}>{opt.label}</span>
                      <span style={{ display: 'block', fontSize: '0.74rem', color: '#64748b' }}>{opt.desc}</span>
                    </span>
                  </label>
                ))}
                <button
                  onClick={() => downloadSalesReport(exportCollected, exportPending)}
                  disabled={!exportCollected && !exportPending}
                  style={{
                    marginTop: '0.75rem', width: '100%', padding: '9px', borderRadius: '8px', border: 'none',
                    fontSize: '0.86rem', fontWeight: 600, color: 'white',
                    cursor: (!exportCollected && !exportPending) ? 'not-allowed' : 'pointer',
                    background: (!exportCollected && !exportPending) ? '#cbd5e1' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  }}
                >
                  Download Excel
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Month navigation */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '1rem 1.5rem', marginBottom: '1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <button
          onClick={() => setSelectedMonth(prevMonth(selectedMonth))}
          style={{
            padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: '8px',
            background: 'white', cursor: 'pointer', fontSize: '0.88rem', color: '#475569',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          ← Previous
        </button>

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
            {monthLabel(selectedMonth)}
          </div>
          {isCurrentMonth && (
            <span style={{
              fontSize: '0.72rem', backgroundColor: '#dbeafe', color: '#1e40af',
              padding: '2px 8px', borderRadius: '9999px', fontWeight: 600,
            }}>
              Current Month
            </span>
          )}
        </div>

        <button
          onClick={() => setSelectedMonth(nextMonth(selectedMonth))}
          disabled={isCurrentMonth}
          style={{
            padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: '8px',
            background: 'white', cursor: isCurrentMonth ? 'not-allowed' : 'pointer',
            fontSize: '0.88rem', color: isCurrentMonth ? '#cbd5e1' : '#475569',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          Next →
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Bills', value: filtered.length, sub: `₹${fmtAmount(totalAmount)}`, bg: '#f8fafc', border: '#e2e8f0', color: '#0f172a', subColor: '#64748b' },
          { label: 'Pending', value: pendingCount, sub: `₹${fmtAmount(totalPending)}`, bg: '#fef3c7', border: '#fcd34d', color: '#92400e', subColor: '#b45309' },
          { label: 'Collected', value: collectedCount, sub: `₹${fmtAmount(totalCollected)}`, bg: '#dcfce7', border: '#86efac', color: '#15803d', subColor: '#16a34a' },
          {
            label: 'Collection Rate',
            value: filtered.length ? `${Math.round((collectedCount / filtered.length) * 100)}%` : '—',
            sub: `${collectedCount} of ${filtered.length} bills`,
            bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8', subColor: '#1d4ed8',
          },
        ].map(({ label, value, sub, bg, border, color, subColor }) => (
          <div key={label} style={{
            backgroundColor: bg, border: `1px solid ${border}`,
            borderRadius: '10px', padding: '1rem 1.25rem',
          }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {label}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color, marginTop: '4px' }}>{value}</div>
            <div style={{ fontSize: '0.8rem', color: subColor, marginTop: '2px' }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Filters. The row wraps so the multi-select panel can drop underneath
          the search box and status buttons at full width. */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '0.85rem 1.25rem', marginBottom: '1rem',
        display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap',
      }}>
        <input
          type="text"
          placeholder="Search patient, client, bill no…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1, padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: '8px',
            fontSize: '0.88rem', outline: 'none',
          }}
        />
        {/* Status lives in the panel with everything else — as a multi-select,
            so Pending + Processed can be asked for together. */}
        <CollectionFilters
          bills={bills}
          filteredBills={filtered}
          title="Payment Collections"
          filters={columnFilters}
          onFiltersChange={setColumnFilters}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
          year={Number(selectedMonth.split('-')[0])}
          month={Number(selectedMonth.split('-')[1])}
        />
      </div>

      {/* Bills table */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
            {bills.length === 0
              ? `No bills found for ${monthLabel(selectedMonth)}`
              : 'No bills match the current filter'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Bill No', 'Duty Type', 'Client', 'Type', 'Invoice Period', 'Amount', 'Pending', 'Status', 'Case Manager', 'Exp. Payment Date', 'Actions'].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.72rem',
                      fontWeight: 600, color: '#64748b', textTransform: 'uppercase',
                      letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((bill) => {
                const typeMeta = bill.bill_type ? BILL_TYPE_META[bill.bill_type] : null;
                const pending = bill.amount_received > 0 ? Math.max(bill.total_amount - bill.amount_received, 0) : 0;
                const isPartial = pending > 0;
                const td: React.CSSProperties = { padding: '0.9rem 1rem', fontSize: '0.88rem', color: '#334155', borderBottom: '1px solid #f1f5f9' };
                return (
                  <tr
                    key={bill.id}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    {/* Bill No */}
                    <td style={{ ...td, fontWeight: 600 }}>
                      {bill.bill_type === 'consumable_return'
                        ? bill.invoice_no || `R-${bill.bill_no}`
                        : bill.invoice_no || `#${bill.bill_no}`}
                    </td>

                    {/* Duty Type */}
                    <td style={td}><DutyTypePill dutyType={bill.duty_type} /></td>

                    {/* Client */}
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {/* Every client on the case, not just the first. */}
                        <span style={{ fontWeight: 700 }}>
                          {(bill.client_names && bill.client_names.length > 0
                            ? bill.client_names
                            : [bill.client_name || bill.patient_name]).join(' & ')}
                        </span>
                        {bill.is_carryover && (
                          <span style={{
                            fontSize: '0.68rem', fontWeight: 700, padding: '1px 6px',
                            borderRadius: '9999px', backgroundColor: '#fef3c7', color: '#b45309',
                            border: '1px solid #fcd34d', whiteSpace: 'nowrap',
                          }}>
                            Carry Over
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '1px' }}>
                        LHHS{bill.patient_id} · {bill.patient_name}
                      </div>
                    </td>

                    {/* Bill Type */}
                    <td style={td}>
                      {typeMeta ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: '9999px',
                          fontSize: '0.75rem', fontWeight: 600,
                          backgroundColor: typeMeta.bg, color: typeMeta.color,
                          border: `1px solid ${typeMeta.border}`,
                        }}>
                          {typeMeta.label}
                        </span>
                      ) : '—'}
                    </td>

                    {/* Invoice Period */}
                    <td style={{ ...td, fontSize: '0.85rem', color: '#475569' }}>
                      {fmt(bill.invoice_period_start)} – {fmt(bill.invoice_period_end)}
                    </td>

                    {/* Amount */}
                    <td style={{ ...td, fontWeight: 600 }}>₹{fmtAmount(bill.total_amount)}</td>

                    {/* Pending balance */}
                    <td style={td}>
                      {isPartial ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: '6px',
                          fontSize: '0.82rem', fontWeight: 700,
                          backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
                        }}>
                          ₹{fmtAmount(pending)}
                        </span>
                      ) : '—'}
                    </td>

                    {/* Status */}
                    <td style={td}>
                      <span style={statusStyle(effectiveStatus(bill))}>{effectiveStatus(bill)}</span>
                    </td>

                    {/* Case Manager */}
                    <td style={{ ...td, color: '#475569' }}>
                      {bill.case_manager || '—'}
                    </td>

                    {/* Expected Payment Date */}
                    <td style={{ ...td, fontSize: '0.85rem', color: '#475569' }}>
                      {bill.expected_payment_date ? fmt(bill.expected_payment_date) : '—'}
                    </td>

                    {/* Actions */}
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => navigate(`/billing/patient/${bill.patient_id}`, { state: { from: '/billing/collections' } })}
                          style={{
                            padding: '0.3rem 0.7rem', border: 'none', borderRadius: '6px',
                            cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500,
                            backgroundColor: '#dbeafe', color: '#1e40af',
                          }}
                        >
                          View Bills
                        </button>
                        {bill.receipt_pdf_url ? (
                          <button
                            onClick={() => navigate(`/billing/patient/${bill.patient_id}`, { state: { from: '/billing/collections' } })}
                            style={{
                              padding: '0.3rem 0.7rem', border: 'none', borderRadius: '6px',
                              cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500,
                              backgroundColor: '#f5f3ff', color: '#7c3aed',
                            }}
                          >
                            Receipt Saved ✓
                          </button>
                        ) : (
                          <button
                            onClick={() => navigate(`/billing/receipt/${bill.id}`)}
                            style={{
                              padding: '0.3rem 0.7rem', border: 'none', borderRadius: '6px',
                              cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500,
                              backgroundColor: '#f0fdf4', color: '#15803d',
                            }}
                          >
                            Record Payment
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer note */}
      {!loading && filtered.length > 0 && (
        <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#94a3b8', textAlign: 'right' }}>
          Showing {filtered.length} bill{filtered.length !== 1 ? 's' : ''} for {monthLabel(selectedMonth)}
          {filtered.length !== bills.length ? ` (filtered from ${bills.length})` : ''}
          {bills.filter(b => b.is_carryover).length > 0 && (
            <span style={{ marginLeft: '8px', color: '#b45309' }}>
              · {bills.filter(b => b.is_carryover).length} carried over from previous months
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentCollectionPage;
